import React, { useEffect, useMemo, useState } from 'react';
import { BrandParamsType, BrandType, ShopType } from 'utils/types';
import { apiService } from 'services/base.service';
import { useNavigate } from 'react-router';
import useDashboardContext from 'contexts/dashboard.context';
import { Paginate, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import PageText from 'components/base/page-text';
import SearchNav from 'components/search-nav';
import DeleteModal from 'components/base/delete-modal';
import { cleanObject, handleApiError, isFCShopCategory } from 'utils/functions';
import AddNewBrandModal from 'components/add-new-brand-modal';
import { ENUM_BRAND_TYPE, ENUM_SHOP_STATUS } from 'utils/enum';
import BrandCard from 'components/brand-card';
import { BrandCardOptionType } from 'components/brand-card/brand-card-options';

type Item = BrandType | ShopType;

const BrandsList = ({
  editKitchenBrands,
  setEditKitchenBrands
}: {
  editKitchenBrands: boolean;
  setEditKitchenBrands: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [showMoveToChowdeckModal, setShowMoveToChowdeckModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);
  const [activeBrandPartnership, setActiveBrandPartnership] = useState<ENUM_BRAND_TYPE>();
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [selectedShop, setSelectedShop] = useState<ShopType | BrandType>();
  const [selectedShops, setSelectedShops] = useState<string[]>([]);
  const [shops, setShops] = useState<{ data: ShopType[]; pagination: any }>({
    data: [],
    pagination: {}
  });

  const [pageCount, setPageCount] = useState(1);

  const [hasError, setHasError] = useState(false);
  const { activeCokitchen, routePrefix } = useDashboardContext();
  const { category, isFcb } = useDashboardContext();
  const [activeBrand, setActiveBrand] = useState<BrandType | ShopType | null>(null);
  const [showArchiveBrandModal, setShowArchiveBrandModal] = useState(false);
  const [showArchiveShopModal, setShowArchiveShopModal] = useState(false);
  const isFcShop = isFCShopCategory();
  const navigate = useNavigate();

  const selectedItems = useMemo(() => {
    return isFcShop ? selectedShops : selectedBrands;
  }, [selectedBrands, selectedShops, isFcShop]);

  const archiveBrand = async (refetchBrands?: boolean) => {
    if (refetchBrands) fetchData();

    setShowArchiveBrandModal(!showArchiveBrandModal);
  };

  const archiveShop = async (refetchShops?: boolean) => {
    if (refetchShops) fetchShop();
    setShowArchiveShopModal(!showArchiveShopModal);
  };

  const handleSelectAll = () => {
    if (isFcShop) {
      const allIds = shops?.data?.map((item: ShopType) => item.id) ?? [];
      setSelectedShops(allIds);
    } else {
      const allIds = brands?.map((item: any) => item.id) ?? [];
      setSelectedBrands(allIds);
    }
  };

  const fetchData = async () => {
    if (!category) return;
    try {
      setLoading(true);
      const params: BrandParamsType = {
        is_fcb: isFcb,
        cokitchen_id: isFcb ? '' : activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params: cleanObject(params)
      });
      const sortByArchived = res.data
        ?.filter((b: BrandType) => b.item_type === category.toUpperCase() && !b.archived)
        .sort((a: BrandType, b: BrandType) => b.updated_at.localeCompare(a.updated_at));

      setBrands(sortByArchived);
    } catch {
      setBrands([]);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };
  const fetchShop = async (page?: number) => {
    try {
      setLoading(true);

      const res = await apiService.get('shops', {
        params: {
          size: '10',
          page: page ?? '1'
        }
      });
      if (res.data) {
        setShops(res.data.data);

        setPageCount(Math.ceil(Number(res.data.data.pagination.total) / 10));
      }
    } catch {
      setShops({ data: [], pagination: {} });
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const onEditClick = (brand_id: string) => {
    if (isFcShop) {
      navigate(`${routePrefix}/shop/${brand_id}/details?edit=true`);
      return;
    }
    navigate(`${routePrefix}/brand/${brand_id}/details?edit=true`);
  };

  const handleArchiveBrand = async () => {
    setDeleteLoading(true);
    try {
      let url;
      let payload;

      if (selectedBrands.length !== 0) {
        url = 'brand_management/multiple-brand/archived';
        payload = {
          brand_ids: selectedBrands,
          archived: true
        };
      } else {
        url = 'brand_management/brand/archived';
        payload = {
          brand_id: activeBrand?.id,
          archived: true
        };
      }
      const res = await apiService.post(url, { data: payload });

      if (res.data) {
        toast(
          <ToastMessage
            type='success'
            message={`${
              selectedBrands.length > 0 ? 'Items' : activeBrand?.name
            } archived successfully`}
          />
        );
        setSelectedBrands([]);
        archiveBrand(true);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
    setDeleteLoading(false);
  };

  const handleArchiveShop = async () => {
    const shopIds = selectedShops.length
      ? selectedShops
      : selectedShop?.id
      ? [selectedShop.id]
      : [];

    if (!shopIds.length) return;

    setDeleteLoading(true);

    try {
      const url = 'shops/status/bulk';
      const payload = {
        shop_ids: shopIds,
        status: ENUM_SHOP_STATUS.archived
      };

      await apiService.put(url, { data: payload });

      toast(
        <ToastMessage
          type='success'
          message={`${shopIds.length > 1 ? 'Shops' : selectedShop?.name} archived successfully`}
        />
      );

      setSelectedShops([]);
      setSelectedShop(undefined);
      setShowArchiveShopModal(false);

      fetchShop();
    } catch (err) {
      handleApiError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, item: Item) => {
    if ('cokitchen_id' in item) {
      setSelectedBrands((prev) =>
        e.target.checked ? [...(prev || []), item.id] : (prev || []).filter((b) => b !== item.id)
      );
    } else {
      setSelectedShops((prev) =>
        e.target.checked ? [...(prev || []), item.id] : (prev || []).filter((s) => s !== item.id)
      );
    }
  };

  const editBulk = () => {
    navigate(`${routePrefix}/brands/edit-multiple-brands`, { state: selectedBrands });
  };
  const onMoveBrand = async () => {
    try {
      setMoveLoading(true);
      const res: any = await apiService.post('brand-partnership/brand-partnership-move', {
        data: {
          brandIds: selectedBrands.length === 0 ? [activeBrand?.id] : selectedBrands,
          brand_type: activeBrandPartnership
        },
        isInternal: true
      });
      if (res?.data) {
        toast(
          <ToastMessage
            type='success'
            message={`Brands moved to ${activeBrandPartnership} successfully`}
          />
        );
        setShowMoveToChowdeckModal(false);
        setSelectedBrands([]);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setMoveLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeCokitchen, isFcb]);
  useEffect(() => {
    if (!isFcShop) return;
    fetchShop();
  }, [pageCount, isFcShop]);

  const getSelectedPageData = async (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    await fetchShop(pageNo);
  };

  const allData = useMemo(() => {
    if (isFcShop) {
      return shops.data?.filter((shop) => shop.status !== ENUM_SHOP_STATUS.archived) ?? [];
    }
    return brands ?? [];
  }, [brands, shops.data, isFcShop]);

  const handleBrandOption = (option: BrandCardOptionType, item: ShopType | BrandType) => {
    switch (option) {
      case 'archive':
        if (isFcShop) {
          setSelectedShop(item);
          setShowArchiveShopModal(true);
        } else {
          setActiveBrand(item);
          setShowArchiveBrandModal(true);
        }
        break;

      case 'edit':
        onEditClick(item.id);
        break;

      case 'select':
        setActiveBrand(item);
        break;

      default:
        break;
    }
  };

  return (
    <>
      {loading ? (
        <PageText text='Loading...' />
      ) : allData.length > 0 ? (
        <div>
          <div className='grid grid-cols-[repeat(auto-fill,_minmax(230px,_1fr))] gap-5'>
            {allData?.map((item) => (
              <BrandCard
                key={item.id}
                data={item}
                options={['edit', 'archive', 'select']}
                isChecked={selectedItems.includes(item.id)}
                onCheck={(e) => handleChange(e, item)}
                onClick={(option) => handleBrandOption(option, item)}
                onMove={(type) => {
                  setActiveBrandPartnership(type);
                  setShowMoveToChowdeckModal(true);
                }}
              />
            ))}
          </div>
        </div>
      ) : (
        <PageText
          text={hasError ? 'Error fetching brands list. Contact tech support' : 'No brands to show'}
        />
      )}
      {selectedItems.length > 0 && (
        <SearchNav
          isBrand={!isFcShop}
          count={selectedItems.length}
          handleSelectAll={handleSelectAll}
          onDeleteClick={() => (isFcShop ? archiveShop(false) : archiveBrand(false))}
          onDoneClick={() => (isFcShop ? setSelectedShops([]) : setSelectedBrands([]))}
          onEditClick={editBulk}
          onMoveBrand={(type) => {
            setActiveBrandPartnership(type);
            setShowMoveToChowdeckModal(true);
          }}
        />
      )}

      <DeleteModal
        onClose={archiveBrand}
        show={showArchiveBrandModal}
        description={`Are you sure you want to archive ${
          selectedBrands.length > 1 ? 'brands' : activeBrand?.name
        }`}
        handleDelete={handleArchiveBrand}
        loading={deleteLoading}
        title={`Archive  ${
          selectedBrands.length > 1 ? `${selectedBrands.length} brands` : activeBrand?.name
        }?`}
        type='archive'
      />
      <DeleteModal
        onClose={archiveShop}
        show={showArchiveShopModal}
        description={`Are you sure you want to archive ${
          selectedShops.length > 1 ? 'shops' : selectedShop?.name
        }`}
        handleDelete={handleArchiveShop}
        loading={deleteLoading}
        title={`Archive ${
          selectedShops.length > 1 ? `${selectedShops.length} shops` : selectedShop?.name
        }?`}
        type='archive'
      />

      <DeleteModal
        onClose={() => setShowMoveToChowdeckModal(false)}
        show={showMoveToChowdeckModal}
        description={`Are you sure you want to move ${
          selectedBrands.length > 1 ? 'brands' : activeBrand?.name
        } to ${activeBrandPartnership} `}
        handleDelete={onMoveBrand}
        loading={moveLoading}
        title={`Move ${
          selectedBrands.length > 1 ? `${selectedBrands.length} brands` : activeBrand?.name
        } to ${activeBrandPartnership} ?`}
        type='move'
      />
      {editKitchenBrands && (
        <AddNewBrandModal refresh={fetchData} show onClose={setEditKitchenBrands} />
      )}
      {isFcShop && (
        <div className='flex justify-center mt-8'>
          <Paginate pageCount={pageCount} onPageChange={getSelectedPageData} />
        </div>
      )}
    </>
  );
};

export default BrandsList;
